/**
 * Admin impersonation sessions.
 *
 * The Django admin's "login as user" mints a JSON Web Token pair for the
 * target user and hands it to /admin-login, which parks both halves in
 * localStorage. Until this module existed only the access token was ever
 * read: it lives for a week, the refresh token for sixty days, and when the
 * access token expired every request began failing with `token_not_valid`
 * while the refresh token sat unused. Because the API's first authenticator
 * turned that rejection into a 403 rather than a 401, nothing recognised it
 * as "session over" either — the billing page just retried into Sentry
 * dozens of times a minute.
 *
 * Everything that touches the admin-session keys goes through here so the
 * refresh path and the AuthContext agree on what a session is.
 */
import { addBreadcrumb, captureError, logWarn } from '../services/sentry';
import { API_V1_BASE_URL } from './baseUrls';

const SESSION_KEY = 'flashtools_admin_session';
const ACCESS_KEY = 'flashtools_admin_access_token';
const REFRESH_KEY = 'flashtools_admin_refresh_token';
const EMAIL_KEY = 'flashtools_admin_email';

/** Refresh this far ahead of `exp` so a request never leaves with a token that dies in flight. */
const REFRESH_AHEAD_SECONDS = 60;

/** The simplejwt refresh view lives under /users, a sibling of /flashtools. */
const REFRESH_URL = `${API_V1_BASE_URL}/users/token/refresh/`;

export function isAdminSession(): boolean {
  try {
    return localStorage.getItem(SESSION_KEY) === 'true';
  } catch {
    return false;
  }
}

export function getAdminAccessToken(): string | null {
  try {
    return localStorage.getItem(ACCESS_KEY);
  } catch {
    return null;
  }
}

export function getAdminEmail(): string | null {
  try {
    return localStorage.getItem(EMAIL_KEY);
  } catch {
    return null;
  }
}

export function storeAdminSession(accessToken: string, refreshToken: string, email: string): void {
  localStorage.setItem(SESSION_KEY, 'true');
  localStorage.setItem(ACCESS_KEY, accessToken);
  localStorage.setItem(REFRESH_KEY, refreshToken);
  localStorage.setItem(EMAIL_KEY, email);
}

export function clearAdminSession(): void {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(EMAIL_KEY);
}

/** `exp` claim of a JWT in epoch seconds, or null when it cannot be read. */
export function jwtExpiry(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const exp = JSON.parse(json).exp;
    return typeof exp === 'number' ? exp : null;
  } catch {
    return null;
  }
}

export function tokenExpiresSoon(token: string, aheadSeconds = REFRESH_AHEAD_SECONDS): boolean {
  const exp = jwtExpiry(token);
  if (exp === null) return false; // Unreadable: let the server be the judge.
  return exp - Date.now() / 1000 < aheadSeconds;
}

/** True for the body simplejwt sends when an access token is expired or malformed. */
export function isTokenNotValid(body: unknown): boolean {
  return Boolean(body && typeof body === 'object' && (body as { code?: unknown }).code === 'token_not_valid');
}

let inflight: Promise<string | null> | null = null;

/**
 * Trade the stored refresh token for a new access token. Single-flight: a
 * page that fires five requests into an expired token gets one refresh, not
 * five, and simplejwt's rotate-on-refresh setting (if ever enabled) would
 * otherwise invalidate all but the first.
 *
 * Resolves to the new access token, or null when the session cannot be
 * revived — no refresh token, refresh token expired, network down.
 */
export function refreshAdminAccessToken(): Promise<string | null> {
  if (inflight) return inflight;
  inflight = doRefresh().finally(() => {
    inflight = null;
  });
  return inflight;
}

async function doRefresh(): Promise<string | null> {
  let refreshToken: string | null = null;
  try {
    refreshToken = localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
  if (!refreshToken) {
    logWarn('Admin session has no refresh token to renew with');
    return null;
  }

  try {
    const res = await fetch(REFRESH_URL, {
      method: 'POST',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify({ refresh: refreshToken }),
    });
    if (!res.ok) {
      logWarn('Admin session refresh was refused', { status: res.status });
      return null;
    }
    const data = (await res.json()) as { access?: string; refresh?: string };
    if (!data.access) {
      logWarn('Admin session refresh returned no access token');
      return null;
    }
    localStorage.setItem(ACCESS_KEY, data.access);
    // simplejwt only returns a new refresh token when rotation is on; keep
    // the current one otherwise.
    if (data.refresh) localStorage.setItem(REFRESH_KEY, data.refresh);
    addBreadcrumb('auth', 'Admin session access token renewed');
    return data.access;
  } catch (error) {
    captureError(error, { where: 'refreshAdminAccessToken' });
    return null;
  }
}

/**
 * The session is over and cannot be renewed. Drop it and send the admin to
 * the login screen with a reason, so they know to relaunch impersonation from
 * the admin panel rather than wonder why every page is empty.
 */
export function endExpiredAdminSession(): void {
  clearAdminSession();
  addBreadcrumb('auth', 'Admin session expired and could not be renewed');
  window.location.href = '/login?error=session_expired';
}
